import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { setSession } from "@/lib/auth";
import api from "@/lib/api";
import { applyPendingReferral } from "@/lib/referral";

/**
 * OAuth landing page.
 *   - Case A (real n8n): n8n redirects here with ?session=<jwt>&username=<u>
 *   - Case B (fallback): no session token → send user back to /login
 *
 * After setting the session we check whether this merchant already has an
 * active plan. Returning merchants (e.g. logged out and logged back in) go
 * straight to /dashboard; only merchants with no plan yet are sent to /plans.
 */
export default function OAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const token = params.get("session") || params.get("token");
    const username = params.get("username") || "";
    if (token) {
      setSession(token, username);
      (async () => {
        await applyPendingReferral();
        try {
          const { data } = await api.get("/me");
          navigate(data.plan_selected ? "/dashboard" : "/plans", { replace: true });
        } catch {
          // /me failing right after setting a fresh session is unexpected,
          // but fall back to /plans rather than stranding the user here.
          navigate("/plans", { replace: true });
        }
      })();
    } else {
      const t = setTimeout(() => navigate("/login", { replace: true }), 1200);
      return () => clearTimeout(t);
    }
  }, [params, navigate]);

  return (
    <div className="grid min-h-screen place-items-center bg-cream">
      <div className="text-center">
        <Loader2 className="mx-auto animate-spin text-hotpink" size={40} />
        <p className="mt-4 font-display text-2xl tracking-tighter">
          Wrapping up your Instagram connection…
        </p>
      </div>
    </div>
  );
}
